import { useModal } from '_hooks/useModal'
import { setUserToken } from '_utils/localData'
import moment from 'moment'
import { useRef, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import { useGuestRegistration } from './useGuestRegistration'

export const useShareOrderFlow = ({
  isGuestCheckout,
  guestFormData,
  eventDetail,
  addressBook,
  submission,
  hasSelectedProductsToPurchase,
  checkExistingGuestUser,
  openRegisterModal,
  userInGroup,
  hasRequestedJoin,
  setShowModalJoin,
}) => {
  const { addToast } = useToasts()
  const [guestAddressData, setGuestAddressData] = useState(null)
  const guestAddressFormRef = useRef(null)

  const { registerGuestAndGetToken } = useGuestRegistration()

  const {
    isOpen: isGuestAddressModalOpen,
    openModal: openGuestAddressModal,
    closeModal: closeGuestAddressModal,
  } = useModal()

  const handleGuestAddressSubmit = async () => {
    if (guestAddressFormRef.current) {
      const isValid = await guestAddressFormRef.current.validate()
      if (!isValid) return
    }

    closeGuestAddressModal()
    submission.setIsSubmitted(true)
    const token = await registerGuestAndGetToken(
      guestFormData,
      submission.setComment,
      setUserToken
    )

    if (!token) {
      submission.setIsSubmitted(false)
      return
    }

    const { address, addressLabel, postalCode, country, deliveryDate, consignee, phone } =
      guestAddressData || {}
    let formattedDate = null
    if (deliveryDate) {
      formattedDate = moment(deliveryDate).format('DD-MM-YYYY')
    }

    const addressOverride = {
      address: address || addressLabel || '',
      postalCode,
      country,
      deliveryDate: formattedDate,
      consignee,
      phone,
    }

    await submission.proceedWithOrder(addressOverride, guestFormData?.noteToHost || null)
  }

  const proceedOrderFlow = async () => {
    try {
      if (!hasSelectedProductsToPurchase()) {
        submission.setError('Please select at least 1 item before entering delivery address')
        return
      }

      submission.setError('')

      if (isGuestCheckout) {
        const hasExistingAccount = await checkExistingGuestUser()
        if (hasExistingAccount) {
          return
        }

        if (guestFormData?.createAccount) {
          openRegisterModal()
          return
        }

        if (eventDetail.bookingAddressRequired) {
          openGuestAddressModal()
          return
        }

        const token = await registerGuestAndGetToken(
          guestFormData,
          submission.setComment,
          setUserToken
        )
        if (!token) {
          return
        }
      }

      if (eventDetail.bookingAddressRequired) {
        if (!addressBook.userAddresses || addressBook.userAddresses.length === 0) {
          addressBook.handleOpenAddNewAddress()
        } else {
          addressBook.toggleChangeAddressModal()
        }
      } else {
        await submission.proceedWithOrder(
          null,
          isGuestCheckout ? guestFormData?.noteToHost || null : null
        )
      }
    } catch (err) {
      addToast(err?.msgResp || 'Something went wrong!', { appearance: 'error', autoDismiss: true })
      submission.setIsSubmitted(false)
    }
  }

  const handleSubmitOrder = async () => {
    if (!hasSelectedProductsToPurchase()) {
      submission.setError('Please select at least 1 item before entering delivery address')
      return
    }

    if (!userInGroup && !isGuestCheckout && !hasRequestedJoin) {
      setShowModalJoin(true)
      return
    }

    await proceedOrderFlow()
  }

  return {
    guestAddressData,
    setGuestAddressData,
    guestAddressFormRef,
    isGuestAddressModalOpen,
    closeGuestAddressModal,
    handleGuestAddressSubmit,
    proceedOrderFlow,
    handleSubmitOrder,
  }
}
